import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button';
import BasicInput from '@/components/BasicInput';
import PasswordInput from '@/components/PasswordInput';
import PasswordRequirements from '@/components/PasswordRequirements';
import SocialLoginButtons from '@/components/SocialLoginButtons';
import EmailVerification from '@/components/EmailVerification';
import Checkbox from '@/components/Checkbox';
import * as auth from '@/api/auth';
import useAuthStore from '@/stores/authStore';
import { useToast } from '@/components/ToastProvider';
import { validateEmail, validatePassword } from '@/utils/validationUtils';
import { trackEvent } from '@/utils/googleAnalytics';

const SignupPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (field, value) => {
    // If the form was submitted, clear only the error for this field
    if (isSubmitted) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    // Update the field value
    switch(field) {
      case 'email':
        setEmail(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'confirmPassword':
        setConfirmPassword(value);
        break;
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Check for empty fields
    if (!email) {
      newErrors.email = t('auth.signup.errors.email_required');
    } else if (!validateEmail(email)) {
      newErrors.email = t('auth.signup.errors.email_invalid');
    }

    if (!password) {
      newErrors.password = t('auth.signup.errors.password_required');
    } else {
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.length) {
        newErrors.password = t('auth.signup.errors.password_length');
      } else if (!passwordValidation.uppercase) {
        newErrors.password = t('auth.signup.errors.password_uppercase');
      } else if (!passwordValidation.number) {
        newErrors.password = t('auth.signup.errors.password_number');
      }
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t('auth.signup.errors.confirm_password_required');
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = t('auth.signup.errors.passwords_not_match');
    }

    if (!termsAccepted) {
      newErrors.terms = t('auth.signup.errors.terms_required');
    }

    return newErrors;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsLoading(true);
    
    try {
      await auth.sendEmailVerificationForSignup(email);
      setIsEmailSent(true);
    } catch (error) {
      console.error('Verification request failed:', error);
      if (error.response?.status === 400) {
        setErrors({
          email: t('auth.signup.errors.email_exists')
        });
      } else {
        setErrors({
          general: t('auth.signup.errors.general')
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSubmit = async () => {
    if (!verificationCode) {
      setErrors({
        verificationCode: t('auth.signup.verification.code_required')
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await auth.signup(email, password, verificationCode);
      setAccessToken(response.accessToken, true);
      trackEvent('user_registered', {
        dondon_uid: response.userId,
      });
      navigate('/verified');
    } catch (error) {
      console.error('Verification failed:', error);
      if (error.response?.status === 400) {
        if (error.response?.data?.code === 'user_already_exists') {
          setErrors({
            verificationCode: t('auth.signup.errors.user_already_exists')
          });
        } else {
          setErrors({
            verificationCode: t('auth.signup.verification.invalid_code')
          });
        }
      } else {
        setErrors({
          general: t('auth.signup.errors.general')
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    try {
      await auth.sendEmailVerificationForSignup(email);
      // Show success toast
      toast(t('auth.signup.verification.resend_success'));
    } catch (error) {
      console.error('Resend code failed:', error);
      // Show error toast
      toast(t('auth.signup.verification.resend_error'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="container py-8">
        <EmailVerification
          email={email}
          verificationCode={verificationCode}
          setVerificationCode={setVerificationCode}
          onVerify={handleVerificationSubmit}
          onResend={handleResendCode}
          errors={errors}
          isLoading={isLoading}
        />
      </div>
    );
  }

  return (
    <div className="container py-8">
      <div className="w-[406px] mx-auto flex flex-col items-center gap-8">
        <h1 className="text-2xl font-bold text-[#111111]">
          {t('auth.signup.title')}
        </h1>
        
        <form onSubmit={handleSignup} className="w-full flex flex-col gap-4" noValidate>
          <div className="flex flex-col gap-3">
            <BasicInput
              title={t('auth.signup.email')}
              type="email"
              placeholder={t('auth.signup.email_placeholder')}
              containerClassName="w-full"
              value={email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={errors.email}
              required
              maxLength={40}
            />

            <PasswordInput
              title={t('auth.signup.password')}
              placeholder={t('auth.signup.password_placeholder')}
              containerClassName="w-full"
              value={password}
              onChange={(e) => handleChange('password', e.target.value)}
              error={errors.password}
              required
            >
              <PasswordRequirements password={password} />
            </PasswordInput>

            <PasswordInput
              title={t('auth.signup.reenter_password')}
              placeholder={t('auth.signup.reenter_password_placeholder')}
              containerClassName="w-full"
              value={confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              error={errors.confirmPassword}
              required
            />
          </div>

          <div className="flex items-center gap-2 my-4">
            <Checkbox 
              checked={termsAccepted}
              onCheckedChange={setTermsAccepted}
            />
            <div className="text-sm ml-2 flex-1">
              <span className="text-[#111111]">{t('auth.signup.terms_agreement')}</span>{' '}
              <a href="/terms/terms_of_use" target="_blank" rel="noopener noreferrer" className="text-[#ff5501]">{t('auth.signup.terms_of_service')}</a>{' '}
              <span className="text-[#111111]">{t('auth.signup.and')}</span>{' '}
              <a href="/terms/privacy_policy" target="_blank" rel="noopener noreferrer" className="text-[#ff5501]">{t('auth.signup.privacy_policy')}</a>{' '}
              <span className="text-[#111111]">{t('auth.signup.terms_agreement_ending')}</span>
            </div>
          </div>
          {errors.terms && (
            <div className="text-[#ff5501] text-xs mb-4">
              {errors.terms}
            </div>
          )}

          <div className="flex flex-col">
            <Button 
              variant="primary" 
              size="xl" 
              className="w-full"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? t('common.loading') : t('auth.signup.submit')}
            </Button>

            <SocialLoginButtons />

            <div className="w-full px-6 flex items-center gap-5 my-4">
              <div className="flex-1 h-[1px] bg-[#f4f4f4]" />
              <div className="text-[#aaaaaa] text-sm">
                {t('auth.signup.have_account')}
              </div>
              <div className="flex-1 h-[1px] bg-[#f4f4f4]" />
            </div>

            <Button 
              variant="default" 
              size="xl" 
              className="w-full"
              as={Link}
              to="/login"
            >
              <span className="text-lg font-bold">
                {t('auth.signup.login')}
              </span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupPage; 